import { useMemo } from "react";
import { ClockOnlineLog, ClockOnlineStatus } from "../../../../../api/admin";
import DSBox from "../../../../system-design/DSBox";
import DSTypography from "../../../../system-design/DSTypography";
import DSStack from "../../../../system-design/DSStack";
import DSTooltip from "../../../../system-design/DSTooltip";

const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const LABEL_WIDTH = 90;
const DATE_LIMIT_DAYS: Record<string, number> = {
  "1 DAY": 1,
  "2 DAY": 2,
  "7 DAY": 7,
  "30 DAY": 30,
  "60 DAY": 60,
};
const STATUS_COLORS: Record<ClockOnlineStatus, string> = {
  [ClockOnlineStatus.ONLINE]: "success.main",
  [ClockOnlineStatus.OFFLINE]: "error.main",
};

type Segment = {
  status: ClockOnlineStatus;
  leftPercent: number;
  widthPercent: number;
  startTime: string;
  endTime: string;
  duration: string;
};

function formatDuration(milliseconds: number): string {
  const totalMinutes = Math.round(milliseconds / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

function generateDays(dateLimit: string): Date[] {
  const count = DATE_LIMIT_DAYS[dateLimit] ?? 1;
  return Array.from({ length: count }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - index);
    date.setHours(0, 0, 0, 0);
    return date;
  });
}

function getNextDayStart(dayStart: Date): Date {
  const next = new Date(dayStart);
  next.setDate(next.getDate() + 1);
  return next;
}

function buildSegments(periods: ClockOnlineLog[], dayStart: Date): Segment[] {
  const dayStartMs = dayStart.getTime();
  const dayEndMs = getNextDayStart(dayStart).getTime();
  const dayDurationMs = dayEndMs - dayStartMs;
  const timeFormat: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  return periods.flatMap((period) => {
    const clampedStart = Math.max(
      new Date(period.period_start).getTime(),
      dayStartMs,
    );
    const clampedEnd = Math.min(
      new Date(period.period_end).getTime(),
      dayEndMs,
    );
    if (clampedStart >= clampedEnd) return [];
    const widthPercent = ((clampedEnd - clampedStart) / dayDurationMs) * 100;
    if (widthPercent < 0.05) return [];
    return [
      {
        status: period.status,
        leftPercent: ((clampedStart - dayStartMs) / dayDurationMs) * 100,
        widthPercent,
        startTime: new Date(clampedStart).toLocaleTimeString([], timeFormat),
        endTime: new Date(clampedEnd).toLocaleTimeString([], timeFormat),
        duration: formatDuration(clampedEnd - clampedStart),
      },
    ];
  });
}

const HoursBreakPoints = () => (
  <DSStack direction="row" sx={{ mb: 0.5 }}>
    <DSBox sx={{ width: LABEL_WIDTH, flexShrink: 0 }} />
    <DSBox sx={{ flex: 1, position: "relative", height: 16 }}>
      {HOUR_TICKS.map((hour) => (
        <DSTypography
          key={hour}
          variant="caption"
          sx={{
            position: "absolute",
            left: `${(hour / 24) * 100}%`,
            transform: "translateX(-50%)",
            fontSize: 10,
            color: "text.secondary",
          }}
        >
          {hour}:00
        </DSTypography>
      ))}
    </DSBox>
  </DSStack>
);

const SegmentTooltipContent = ({ segment }: { segment: Segment }) => (
  <DSStack spacing={0.5}>
    <DSTypography
      variant="caption"
      color={STATUS_COLORS[segment.status]}
      fontWeight={600}
    >
      {segment.status}
    </DSTypography>
    <DSTypography variant="caption">
      {segment.startTime} - {segment.endTime}
    </DSTypography>
    <DSTypography variant="caption">Duration: {segment.duration}</DSTypography>
  </DSStack>
);

const DeviceOnlineTimeline = ({
  data,
  dateLimit,
}: {
  data: ClockOnlineLog[];
  dateLimit: string;
}) => {
  const days = useMemo(() => generateDays(dateLimit), [dateLimit]);

  const segmentsByDay = useMemo(() => {
    const map = new Map<number, Segment[]>();
    for (const day of days) {
      map.set(day.getTime(), buildSegments(data, day));
    }
    return map;
  }, [data, days]);

  return (
    <DSBox>
      <HoursBreakPoints />
      {days.map((day) => (
        <DSStack
          key={day.getTime()}
          direction="row"
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <DSTypography
            variant="caption"
            sx={{
              width: LABEL_WIDTH,
              flexShrink: 0,
              pr: 1,
              textAlign: "right",
              color: "text.secondary",
            }}
          >
            {day.toLocaleDateString([], {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </DSTypography>
          <DSBox
            sx={{
              flex: 1,
              height: 24,
              bgcolor: "action.hover",
              borderRadius: 1,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {HOUR_TICKS.filter((hour) => hour > 0 && hour < 24).map((hour) => (
              <DSBox
                key={hour}
                sx={{
                  position: "absolute",
                  left: `${(hour / 24) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  bgcolor: "divider",
                }}
              />
            ))}
            {(segmentsByDay.get(day.getTime()) ?? []).map((segment, index) => (
              <DSTooltip
                key={index}
                title={<SegmentTooltipContent segment={segment} />}
                arrow
                placement="top"
              >
                <DSBox
                  sx={{
                    position: "absolute",
                    left: `${segment.leftPercent}%`,
                    width: `${segment.widthPercent}%`,
                    top: 0,
                    bottom: 0,
                    bgcolor: STATUS_COLORS[segment.status],
                    cursor: "pointer",
                  }}
                />
              </DSTooltip>
            ))}
          </DSBox>
        </DSStack>
      ))}
    </DSBox>
  );
};

export default DeviceOnlineTimeline;
